'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

export default function LoginPage() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen bg-cloud flex items-center justify-center px-4 py-12">
      <div className="fixed inset-0 grid-atmosphere pointer-events-none" />
      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Link href="/home">
            <AppLogo text="Lenders Home" iconName="BanknotesIcon" size={36} className="text-slate" />
          </Link>
        </div>

        <div className="glass-panel rounded-3xl p-8 shadow-form">
          {/* Tabs */}
          <div className="flex bg-cloud-deep rounded-2xl p-1 mb-8">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold font-body transition-all duration-200 ${mode === 'login' ? 'bg-white text-slate shadow-card' : 'text-slate-fog hover:text-slate'}`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold font-body transition-all duration-200 ${mode === 'signup' ? 'bg-white text-slate shadow-card' : 'text-slate-fog hover:text-slate'}`}
            >
              Create Account
            </button>
          </div>

          {mode === 'login' ? <LoginForm /> : <SignupForm />}

          <p className="mt-6 text-center text-xs text-slate-fog font-body">
            By continuing, you agree to Lenders Home LLC&apos;s{' '}
            <span className="text-sky cursor-pointer hover:underline">Terms of Service</span>
            {' '}and{' '}
            <span className="text-sky cursor-pointer hover:underline">Privacy Policy</span>
          </p>
        </div>

        <p className="text-center mt-6 text-sm text-slate-fog font-body">
          <Link href="/home" className="text-sky hover:underline">← Back to home</Link>
        </p>
      </div>
    </div>
  );
}

function LoginForm() {
  const router = useRouter();
  const { signIn } = useAuth();
  const supabase = createClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await signIn(email, password);
      // Check role immediately after sign-in to avoid flash redirect
      const userId = data?.user?.id;
      if (userId) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', userId)
          .single();
        if (profile?.role === 'admin') {
          router.push('/admin');
          return;
        }
      }
      router.push('/dashboard');
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 font-body">
          {error}
        </div>
      )}
      <div>
        <label className="block text-sm font-semibold text-slate mb-1.5 font-body">Email Address</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="you@example.com"
          className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-white text-slate placeholder-slate-fog text-sm font-body focus:outline-none focus:ring-2 focus:ring-sky/40 focus:border-sky transition-all"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate mb-1.5 font-body">Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          placeholder="••••••••"
          className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-white text-slate placeholder-slate-fog text-sm font-body focus:outline-none focus:ring-2 focus:ring-sky/40 focus:border-sky transition-all"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full py-3.5 bg-sky text-white rounded-xl text-sm font-semibold font-body hover:bg-sky-dark transition-all duration-200 shadow-sky/30 shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? 'Signing in...' : 'Sign In'}
      </button>
    </form>
  );
}

function SignupForm() {
  const router = useRouter();
  const { signUp } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await signUp(email, password, { fullName });
      router.push('/dashboard');
    } catch (err: any) {
      setError(err?.message || 'Could not create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 font-body">
          {error}
        </div>
      )}
      <div>
        <label className="block text-sm font-semibold text-slate mb-1.5 font-body">Full Name</label>
        <input
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
          placeholder="John Martinez"
          className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-white text-slate placeholder-slate-fog text-sm font-body focus:outline-none focus:ring-2 focus:ring-sky/40 focus:border-sky transition-all"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate mb-1.5 font-body">Email Address</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="you@example.com"
          className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-white text-slate placeholder-slate-fog text-sm font-body focus:outline-none focus:ring-2 focus:ring-sky/40 focus:border-sky transition-all"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate mb-1.5 font-body">Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          placeholder="Min. 6 characters"
          className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-white text-slate placeholder-slate-fog text-sm font-body focus:outline-none focus:ring-2 focus:ring-sky/40 focus:border-sky transition-all"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate mb-1.5 font-body">Confirm Password</label>
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          placeholder="••••••••"
          className="w-full px-4 py-3 rounded-xl border border-slate-mist bg-white text-slate placeholder-slate-fog text-sm font-body focus:outline-none focus:ring-2 focus:ring-sky/40 focus:border-sky transition-all"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full py-3.5 bg-sky text-white rounded-xl text-sm font-semibold font-body hover:bg-sky-dark transition-all duration-200 shadow-sky/30 shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? 'Creating account...' : 'Create Account'}
      </button>
    </form>
  );
}
